import logger from './vercelRuntimeLogger';

interface UploadOptions {
  maxRetries?: number;
  retryDelay?: number;
  timeout?: number;
  onProgress?: (progress: number) => void;
  onRetry?: (attempt: number, error: Error) => void;
}

/**
 * Enhanced upload function with retry logic and better error handling
 */
export const uploadFileToStorageWithRetry = async (
  file: File,
  fileName: string = "uploaded-model.glb",
  options: UploadOptions = {}
): Promise<string> => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    timeout = 60000, // 60 seconds
    onProgress,
    onRetry
  } = options;

  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  
  logger.info('FileUpload', 'UPLOAD_START', {
    uploadId,
    fileName,
    fileSize: file.size,
    fileType: file.type,
    fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
    maxRetries,
    timeout
  });

  // Helper function to create abort controller with timeout
  const createAbortController = (timeoutMs: number) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      logger.warn('FileUpload', 'REQUEST_TIMEOUT', {
        uploadId,
        timeout: timeoutMs
      });
    }, timeoutMs);
    return { controller, timeoutId };
  };

  // Helper function to perform upload with timeout
  const attemptUpload = async (attempt: number): Promise<string> => {
    try {
      // Get upload URL with proper error handling
      const uploadUrlEndpoint = process.env.REACT_APP_UPLOAD_URL || "https://3d-serverless-eight.vercel.app/api/get-upload-urls";
      
      logger.info('FileUpload', 'GET_UPLOAD_URL_START', {
        uploadId,
        attempt,
        endpoint: uploadUrlEndpoint,
        containerName: "3dstudio",
        fileName
      });

      // Create abort controller for the first request
      const { controller: urlController, timeoutId: urlTimeoutId } = createAbortController(30000); // 30s for URL request
      
      let uploadResponse: Response;
      try {
        uploadResponse = await fetch(
          uploadUrlEndpoint,
          {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Accept": "application/json"
            },
            body: JSON.stringify({
              containerName: "3dstudio",
              fileNames: [fileName],
            }),
            signal: urlController.signal
          }
        );
      } catch (fetchError: any) {
        clearTimeout(urlTimeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timeout: Failed to get upload URL');
        }
        
        // Network error - might be CORS or connectivity issue
        logger.error('FileUpload', 'NETWORK_ERROR', fetchError, {
          uploadId,
          attempt,
          errorType: fetchError.name,
          errorMessage: fetchError.message
        });
        
        // Check if it's a CORS error (typically manifests as TypeError)
        if (fetchError instanceof TypeError && fetchError.message === 'Failed to fetch') {
          throw new Error('Network error: Unable to reach upload service. This might be a CORS issue or the service is unavailable.');
        }
        
        throw fetchError;
      } finally {
        clearTimeout(urlTimeoutId);
      }

      if (!uploadResponse.ok) {
        let errorDetails = '';
        try {
          const errorText = await uploadResponse.text();
          errorDetails = errorText;
        } catch {
          errorDetails = `HTTP ${uploadResponse.status}`;
        }
        
        logger.error('FileUpload', 'GET_UPLOAD_URL_FAILED', 
          new Error(`Failed to get upload URL: ${uploadResponse.status}`), 
          {
            uploadId,
            attempt,
            status: uploadResponse.status,
            statusText: uploadResponse.statusText,
            errorDetails
          }
        );
        
        throw new Error(`Failed to get upload URL (${uploadResponse.status}): ${errorDetails}`);
      }
      
      let data: any;
      try {
        data = await uploadResponse.json();
      } catch (parseError) {
        logger.error('FileUpload', 'PARSE_ERROR', parseError, {
          uploadId,
          attempt,
          responseType: uploadResponse.headers.get('content-type')
        });
        throw new Error('Invalid response format from upload service');
      }
      
      if (!data.uploadUrls || !data.uploadUrls[0]) {
        throw new Error('No upload URL received from service');
      }
      
      logger.info('FileUpload', 'GET_UPLOAD_URL_SUCCESS', {
        uploadId,
        attempt,
        uploadUrlsCount: data.uploadUrls?.length,
        hasUploadUrl: !!data.uploadUrls?.[0]
      });

      // Upload the file to Azure
      const uploadUrl = data.uploadUrls[0];
      const uploadStartTime = Date.now();
      
      logger.info('FileUpload', 'BLOB_UPLOAD_START', {
        uploadId,
        attempt,
        fileName,
        fileSize: file.size,
        contentType: file.type || "model/gltf-binary"
      });

      // Create abort controller for blob upload
      const { controller: blobController, timeoutId: blobTimeoutId } = createAbortController(timeout);
      
      let blobUploadResponse: Response;
      try {
        // Create XMLHttpRequest for progress tracking if needed
        if (onProgress) {
          blobUploadResponse = await uploadWithProgress(uploadUrl, file, onProgress, blobController.signal);
        } else {
          blobUploadResponse = await fetch(uploadUrl, {
            method: "PUT",
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": file.type || "model/gltf-binary",
            },
            body: file,
            signal: blobController.signal
          });
        }
      } catch (uploadError: any) {
        clearTimeout(blobTimeoutId);
        
        if (uploadError.name === 'AbortError') {
          throw new Error(`Upload timeout: File upload took longer than ${timeout}ms`);
        }
        
        logger.error('FileUpload', 'BLOB_UPLOAD_ERROR', uploadError, {
          uploadId,
          attempt,
          uploadDuration: `${Date.now() - uploadStartTime}ms`
        });
        
        throw uploadError;
      } finally {
        clearTimeout(blobTimeoutId);
      }
      
      const uploadDuration = Date.now() - uploadStartTime;
      
      if (!blobUploadResponse.ok) {
        logger.error('FileUpload', 'BLOB_UPLOAD_FAILED',
          new Error(`Blob upload failed: ${blobUploadResponse.status}`),
          {
            uploadId,
            attempt,
            status: blobUploadResponse.status,
            statusText: blobUploadResponse.statusText,
            uploadDuration: `${uploadDuration}ms`
          }
        );
        throw new Error(`Failed to upload file to storage (${blobUploadResponse.status}): ${blobUploadResponse.statusText}`);
      }
      
      logger.info('FileUpload', 'BLOB_UPLOAD_SUCCESS', {
        uploadId,
        attempt,
        uploadDuration: `${uploadDuration}ms`,
        uploadSpeed: `${(file.size / uploadDuration / 1024).toFixed(2)} KB/ms`
      });

      // Return clean URL without query parameters
      const finalUrl = uploadUrl.split('?')[0];
      
      logger.info('FileUpload', 'UPLOAD_COMPLETE', {
        uploadId,
        finalUrl,
        fileName,
        totalDuration: `${Date.now() - parseInt(uploadId.split('_')[1])}ms`,
        attempts: attempt
      });
      
      return finalUrl;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      logger.error('FileUpload', 'UPLOAD_ATTEMPT_FAILED', error, {
        uploadId,
        attempt,
        fileName,
        fileSize: file.size,
        errorMessage
      });
      
      // If we have retries left and it's not a non-retryable error
      if (attempt < maxRetries && !isNonRetryableError(error)) {
        const delay = retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
        
        logger.info('FileUpload', 'RETRY_SCHEDULED', {
          uploadId,
          nextAttempt: attempt + 1,
          delayMs: delay,
          errorMessage
        });
        
        if (onRetry) {
          onRetry(attempt, error instanceof Error ? error : new Error(errorMessage));
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return attemptUpload(attempt + 1);
      }
      
      throw error;
    }
  };

  // Start upload with retry logic
  try {
    return await attemptUpload(1);
  } catch (error) {
    logger.error('FileUpload', 'UPLOAD_FAILED_FINAL', error, {
      uploadId,
      fileName,
      fileSize: file.size,
      maxRetries
    });
    
    // Provide user-friendly error message
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('Network error')) {
        throw new Error('Unable to connect to upload service. Please check your internet connection and try again.');
      }
      if (error.message.includes('timeout')) {
        throw new Error('Upload timed out. Please try again with a smaller file or check your internet connection.');
      }
      if (error.message.includes('CORS')) {
        throw new Error('Upload service configuration error. Please contact support.');
      }
      throw error;
    }
    
    throw new Error('Failed to upload file. Please try again.');
  }
};

// Helper function to upload with progress tracking
async function uploadWithProgress(
  url: string, 
  file: File, 
  onProgress: (progress: number) => void,
  signal: AbortSignal
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Track upload progress
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const progress = (e.loaded / e.total) * 100;
        onProgress(progress);
      }
    });
    
    // Handle completion
    xhr.addEventListener('load', () => {
      const response = new Response(xhr.response, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: new Headers({
          'content-type': xhr.getResponseHeader('content-type') || ''
        })
      });
      resolve(response);
    });
    
    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });
    
    // Handle abort signal
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }
    
    // Start upload
    xhr.open('PUT', url);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'model/gltf-binary');
    xhr.send(file);
  });
}

// Helper to determine if an error should not be retried
function isNonRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  
  // Don't retry authentication errors, bad requests, or permanent failures
  if (message.includes('401') || 
      message.includes('403') || 
      message.includes('400') ||
      message.includes('invalid') ||
      message.includes('not found')) {
    return true;
  }
  
  return false;
}

// Export a wrapper that maintains backward compatibility
export const uploadFileToStorage = uploadFileToStorageWithRetry;